import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import { A4_WIDTH, A4_HEIGHT } from "../constants/layoutConstants";
// import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";

if (typeof window !== "undefined") {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
}

const PDF_A4_WIDTH = 595.28;
const PDF_A4_HEIGHT = 841.89;

const pixelToPoint = (pixel) => {
  return (pixel / A4_WIDTH) * PDF_A4_WIDTH;
};

// Helper function to ensure we have proper ArrayBuffer
const ensureArrayBuffer = (buffer) => {
  if (!buffer) return null;

  // If it's already an ArrayBuffer
  if (buffer instanceof ArrayBuffer) {
    return buffer;
  }

  // If it's a Uint8Array
  if (buffer instanceof Uint8Array) {
    return buffer.buffer.slice(
      buffer.byteOffset,
      buffer.byteOffset + buffer.byteLength
    );
  }

  // If it's an array of numbers (from localStorage)
  if (Array.isArray(buffer)) {
    const uint8Array = new Uint8Array(buffer);
    return uint8Array.buffer;
  }

  // If it's an object with a data property
  if (buffer.data) {
    return ensureArrayBuffer(buffer.data);
  }

  return null;
};

// Download template with field placeholders (for table row download)
export const downloadPDF = async (doc) => {
  try {
    // Get buffer from document
    let buffer = doc.arrayBuffer;

    // If still no buffer, check bufferBytes
    if (!buffer && doc.bufferBytes) {
      buffer = doc.bufferBytes;
    }

    if (!buffer) {
      console.error("No buffer found for document:", doc.id);
      alert("PDF data not found. Please try refreshing the page.");
      return;
    }

    // Ensure proper ArrayBuffer
    const arrayBuffer = ensureArrayBuffer(buffer);
    if (!arrayBuffer) {
      console.error("Failed to convert buffer to ArrayBuffer");
      alert("Invalid PDF data format");
      return;
    }

    // Load the original PDF
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const pages = pdfDoc.getPages();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    // Draw fields on each page as PREVIEW (template style)
    for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
      const pageNum = pageIdx + 1;
      const pdfPage = pages[pageIdx];
      const fieldsOnPage = doc.droppedFields?.[pageNum] || [];

      // Get actual page dimensions
      const pageHeight = pdfPage.getHeight();
      const pageWidth = pdfPage.getWidth();

      for (const field of fieldsOnPage) {
        const x = pixelToPoint(field.x);
        const width = pixelToPoint(field.width || 200);
        const height = pixelToPoint(30);
        // Use actual page height instead of constant
        const y = pageHeight - pixelToPoint(field.y) - height;

        // Draw role badge
        const roleColors = {
          initiator: { bg: [0.56, 0.93, 0.56], text: [0, 0.5, 0] },
          approver: { bg: [0.87, 0.63, 0.87], text: [0.5, 0, 0.5] },
          applicant: { bg: [0.68, 0.85, 0.9], text: [0, 0.3, 0.6] },
        };
        const roleColor = roleColors[field.role] || roleColors.applicant;

        pdfPage.drawRectangle({
          x: x,
          y: y + height + 2,
          width: 60,
          height: 12,
          color: rgb(...roleColor.bg),
        });

        pdfPage.drawText(field.role || "applicant", {
          x: x + 4,
          y: y + height + 5,
          size: 8,
          font: font,
          color: rgb(...roleColor.text),
        });

        // Draw label if enabled
        if (field.showLabel !== false && field.labelPosition === "top") {
          const labelText = `${field.label}${field.required ? " *" : ""}`;
          pdfPage.drawText(labelText, {
            x: x,
            y: y + height + 15,
            size: 10,
            font: boldFont,
            color: rgb(0, 0, 0),
          });
        }

        // Draw field based on type (PREVIEW STYLE)
        if (field.type === "checkbox") {
          pdfPage.drawRectangle({
            x: x + 5,
            y: y + 5,
            width: pixelToPoint(20),
            height: pixelToPoint(20),
            borderColor: rgb(0.7, 0.7, 0.7),
            borderWidth: 1,
            color: rgb(0.96, 0.96, 0.96),
          });
        } else if (field.type === "image") {
          const imgHeight = pixelToPoint(field.height || 100);
          pdfPage.drawRectangle({
            x: x,
            y: y - (imgHeight - height),
            width: width,
            height: imgHeight,
            borderColor: rgb(0.7, 0.7, 0.7),
            borderWidth: 1,
            color: rgb(0.96, 0.96, 0.96),
          });
          pdfPage.drawText("Image field", {
            x: x + 10,
            y: y + (imgHeight - height) / 2 - 5,
            size: 10,
            font: font,
            color: rgb(0.6, 0.6, 0.6),
          });
        } else {
          // Regular input fields
          pdfPage.drawRectangle({
            x: x,
            y: y,
            width: width,
            height: height,
            borderColor: rgb(0.7, 0.7, 0.7),
            borderWidth: 1,
            color: rgb(0.96, 0.96, 0.96),
          });

          const placeholders = {
            text: "Text input",
            name: "Name",
            email: "Email address",
            phone: "Phone number",
            date: "DD/MM/YYYY",
            signature: "Signature",
            select: field.options?.[0] || "Select...",
          };
          const placeholder =
            placeholders[field.type] || field.label || "Input";

          pdfPage.drawText(placeholder, {
            x: x + 8,
            y: y + 10,
            size: (field.fontSize || 12) - 2,
            font: font,
            color: rgb(0.6, 0.6, 0.6),
          });
        }
      }
    }

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${doc.documentName.replace(/\s+/g, "_")}_template.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Template download error:", error);
    console.error("Error stack:", error.stack);
    alert("Failed to download template: " + error.message);
  }
};

// Download filled PDF with values (for applicant/approver views)
export const downloadFilledPDF = async (doc, userFieldData, userId) => {
  try {
    // Get buffer from document
    let buffer = doc.arrayBuffer;

    if (!buffer && doc.bufferBytes) {
      buffer = doc.bufferBytes;
    }

    if (!buffer) {
      console.error("No buffer found for document:", doc.id);
      alert("PDF data not found. Please try refreshing the page.");
      return;
    }

    // Ensure proper ArrayBuffer
    const arrayBuffer = ensureArrayBuffer(buffer);
    if (!arrayBuffer) {
      console.error("Failed to convert buffer to ArrayBuffer");
      alert("Invalid PDF data format");
      return;
    }

    // Load the original PDF
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const pages = pdfDoc.getPages();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    // Collect all field values
    const allFieldValues = {};

    if (userFieldData && typeof userFieldData === "object") {
      Object.entries(userFieldData).forEach(([key, value]) => {
        if (!key.startsWith("_") && !["status", "role"].includes(key)) {
          allFieldValues[key] = value;
        }
      });
    }

    // Draw filled values on each page
    for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
      const pageNum = pageIdx + 1;
      const pdfPage = pages[pageIdx];
      const fieldsOnPage = doc.droppedFields?.[pageNum] || [];

      // Get actual page dimensions
      const pageHeight = pdfPage.getHeight();
      const pageWidth = pdfPage.getWidth();

      for (const field of fieldsOnPage) {
        const x = pixelToPoint(field.x);
        const width = pixelToPoint(field.width || 200);
        const height = pixelToPoint(30);
        // Use actual page height instead of constant
        const y = pageHeight - pixelToPoint(field.y) - height;

        // Draw label if enabled
        if (field.showLabel !== false && field.labelPosition === "top") {
          const labelText = `${field.label}${field.required ? " *" : ""}`;
          pdfPage.drawText(labelText, {
            x: x,
            y: y + height + 2,
            size: 10,
            font: boldFont,
            color: rgb(0, 0, 0),
          });
        }

        // Get field value
        const fieldValue = allFieldValues[field.id] || "";

        // Draw value based on field type
        if (field.type === "checkbox") {
          if (fieldValue === true || fieldValue === "true") {
            pdfPage.drawText("✓", {
              x: x + 5,
              y: y + 8,
              size: 18,
              font: boldFont,
              color: rgb(0, 0.5, 0),
            });
          }
        } else if (field.type === "image" && fieldValue) {
          pdfPage.drawText("[Image]", {
            x: x,
            y: y + 10,
            size: 10,
            font: boldFont,
            color: rgb(0, 0, 0),
          });
        } else if (fieldValue) {
          const fontSize = field.fontSize || 12;
          const textFont =
            field.type === "signature"
              ? await pdfDoc.embedFont(StandardFonts.TimesRomanBoldItalic)
              : boldFont;

          pdfPage.drawText(String(fieldValue), {
            x: x,
            y: y + 10,
            size: fontSize,
            font: textFont,
            color: rgb(0, 0, 0),
            maxWidth: width - 16,
          });
        }
      }
    }

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download =
      userId && userId !== "merged"
        ? `${doc.documentName}_${userId}.pdf`
        : `${doc.documentName}_filled.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Filled PDF download error:", error);
    console.error("Error stack:", error.stack);
    alert("Failed to download filled PDF: " + error.message);
  }
};
